import { useAuth } from 'react-oidc-context';
import { Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import jwt from "jwt-decode";
import React, { Fragment, useEffect, useState } from "react";
import { AlertMessage, AlertMessageProps } from "@hqy/react-shared-components";
import ErrorPage from "../../pages/ErrorPage";
import UserCredentialsApi from "../../services/Api";
import { defaultLimit } from "../../services/Constatnts";
import { NetworkError, ValidationError } from "../../services/Errors";
import {
  Brand,
  ChangeLogResponse,
  CredentialResponse,
  FormActionStatus,
  LoginSupportInfoFormActions,
  Partition,
  PendingActions,
  PredefinedQuestion,
  QuestionAnswer,
  UpdateUserCredentialsRequest,
  UserActivityResponse,
  UserPermissions,
} from "../../services/Types";
import Loading from "../Loading";
import { LoginEmailForm } from "../LoginEmailForm/LoginEmailForm";
import { LoginStatusList } from "../LoginStatusList/LoginStatusList";
import LoginSupportTabs from "../LoginSupportTabs/LoginSupportTabs";
import { LoginUsernameForm } from "../LoginUsernameForm/LoginUsernameForm";
import { MarkVerifiedSection } from "../MarkVerifiedSection/MarkVerifiedSection";
import { PasswordAndMFASection } from "../PasswordAndMFASection/PasswordAndMFASection";
import { UserInfoSection } from "../UserInfoSection/UserInfoSection";

const userCredentialsApi = new UserCredentialsApi();

const useStyles = makeStyles({
  heading: {
    marginBottom: "15px!important",
  },
  mainContent: {
    padding: "10px 27px 33px",
  },
  loginSupportTitle: {
    fontWeight: "400",
    margin: "0 0 12px 0",
  },
  isEmailVerified: {
    float: "right",
  },
  isEmailVerifiedIcon: {
    color: "#FFCC66",
    fontSize: "24px!important",
  },
  markVerifiedButton: {
    "&.MuiButton-root": {
      backgroundColor: "#FFF7E8",
      border: "1px solid #FFCC66",
    },
  },
  emailActionButtons: {
    marginTop: "11px",
  },
  verificationEmailAndStatusHolder: {
    marginTop: "-3px",
  },
  fieldErrorsHolder: {
    position: "relative",
    padding: "0 0 7px 25px",
    color: "#D80443",
    "& i": {
      position: "absolute",
      top: "0",
      left: "0",
    },
  },
  errorBoxWrapper: {
    background: "#fafafa",
  },

  contentHolder: {
    paddingTop: "2%",
    paddingBottom: "2%"
  },
  showcaseValue: {
    paddingLeft: "13px",
  },
  nonVerifiedIcon: {
    transform: "translateY(-50%) rotate(45deg)",
  },
  verifiedIcon: {
    transform: "translateY(-50%)",
  },
  emailVerificationIcon: {
    display: "inline-block",
    position: "absolute",
    right: "20px",
    top: "50%",
  },
  emailInputHolder: {
    position: "relative",
  },
  loaderHolder: {
    position: "relative",
    minHeight: "50vh",
  },
  userIdsHolder: {
    textAlign: "center",
  },
  formButton: {
    display: "inline-block",
    margin: "0 10px !important",
  },
  buttonsHolder: {
    marginTop: "50px",
  },
  expiresOnLabel: {
    paddingLeft: "20px",
  },
  sendVerificationEmailIcon: {
    color: "#006C7D",
  },

  emailVerifiedIcon: {
    color: "#006C7D",
    fontSize: "19px!important",
  },
  emailNotVerified: {
    float: "right",
  },
  emailNotVerifiedIcon: {
    color: "#FFCC66",
    fontSize: "19px!important",
  },
  notice: {
    fontFamily: "Lato",
    fontStyle: "italic",
    fontWeight: 400,
    fontSize: "14px",
    lineHeight: "17px",
    color: "#767676",
  },
  passwordBox: {
    width: "100%",
    height: "148px",
    background: "#EEEEEE",
    borderRadius: "4px",
    display: "table",
    position: "relative",
  },
  passwordBoxDisableOverlay: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
  },
  passwordButtonHolder: {
    display: "table-cell",
    verticalAlign: "middle",
  },
  labelInputDisaled: {
    margin: "20px 0 0",
  },
});

const SupportModalComponent = ({
  credentialId,
  originClient,
  access_token,
  id_token,
  brand,
}: {
  credentialId: string;
  originClient: string | null;
  access_token: string | undefined;
  id_token: string | undefined;
  brand?: Brand;  
}) => {
  const classes = useStyles();
  const { signinRedirect  } = useAuth();

  const [isContentLoading, setIsContentLoading] = useState<boolean>(true);
  const [userCredentials, setUserCredentials] =
    useState<CredentialResponse | null>(null);
  const [userPermissions, setUserPermissions] = useState<
    Array<UserPermissions>
  >([]);
  const [activeEmail, setActiveEmail] = useState<string>("");
  const [predefinedQuestions, setPredefinedQuestions] = useState<
    Array<PredefinedQuestion>
  >([]);
  const [activeAnswer, setActiveAnswer] = useState<string>("");
  const [userCredentialsPendingActions, setUserCredentialsPendingActions] =
    useState<PendingActions>({
      require: [],
      optional: [],
    });

  const [activeUserActivityPage, setActiveUserActivityPage] =
    useState<number>(1);
  const [activeChangeLogPage, setActiveChangeLogPage] = useState<number>(1);
  const [userActivity, setUserActivity] = useState<UserActivityResponse>({
    items: [],
    count: 0,
  });
  const [changeLogs, setChangeLogs] = useState<ChangeLogResponse>({
    items: [],
    count: 0,
  });

  const [alertMessageProps, setAlertMessageProps] = useState<AlertMessageProps | null>(null);
  const [errorOutlines, setErrorOutlines] = useState<any>({});

  const [dataFetchingError, setDataFetchingError] = useState<Error | null>(
    null
  );

  const [formActionsStatus, setFormActionsStatus] =
    useState<LoginSupportInfoFormActions>({
      passwordResetEmail: FormActionStatus.NO_ACTION,
      resetPhone: FormActionStatus.NO_ACTION,
      sendVerificationEmail: FormActionStatus.NO_ACTION,
      verifyOrUpdateEmail: FormActionStatus.NO_ACTION,
      updateUsername: FormActionStatus.NO_ACTION,
    });

  const updateFormAction = (action: string, status: FormActionStatus) => {
    setFormActionsStatus((prevVal) => ({
      ...prevVal,
      [action]: status,
    }));
  };

  const [isMarkVerifiedCollapseOpened, setIsMarkVerifiedCollapseOpened] =
    useState<boolean>(false);

  const isOktaUser = userCredentials?.partition === Partition.OKTA;

  const isSupportUserLoggedIn = (token: string): boolean => {
    if(token && token !== "")
    {
      const jwtPayload = jwt(token) as any;
      const upn = jwtPayload["upn"] as any;
      try {
        return !!upn?.includes("@");
      } catch(e) {
        return false
      }
    }
    else
    {
      return false;
    }
    
  };
  
  useEffect(() => {
    const init = async () => {
      await userCredentialsApi.init(access_token, signinRedirect, originClient);
      const apiRequests: any[] = [
        userCredentialsApi.getCredentialsById(credentialId),
      ];

      if (isSupportUserLoggedIn(userCredentialsApi.getToken())) {
        if(id_token) {
          apiRequests.push(userCredentialsApi.getPermissionsWithIdToken(id_token))
        }
        apiRequests.push(
          userCredentialsApi.getCredentialsStatus(credentialId),
          userCredentialsApi.getChangeLogs(credentialId),
          userCredentialsApi.getUserActivity(credentialId),
          userCredentialsApi.getQuestions()
        );
      }
      else
      {
        apiRequests.push(userCredentialsApi.getPermissions());
      }

      Promise.all(apiRequests)
        .then(
          ([
            credentialResponse,
            userPermissionsResponse,
            credentialsStatusResponse,
            changeLogs,
            userActivityResponse,
            predefinedQuestionsResponse
          ]) => {
            setUserCredentials(credentialResponse);
            setUserPermissions(userPermissionsResponse);
            setActiveEmail(credentialResponse.email);
            credentialsStatusResponse &&
              setUserCredentialsPendingActions(credentialsStatusResponse);
            changeLogs && setChangeLogs(changeLogs);
            userActivityResponse && setUserActivity(userActivityResponse);
            predefinedQuestionsResponse && setPredefinedQuestions(predefinedQuestionsResponse);
            
            if (isSupportUserLoggedIn(userCredentialsApi.getToken()) && credentialResponse?.questions?.[0]?.id) {
              userCredentialsApi
                .getQuestionAnswersById(credentialResponse?.questions?.[0]?.id)
                .then(questionAnswerResponse => {
                  questionAnswerResponse?.answer && setActiveAnswer(questionAnswerResponse.answer);
                  setIsContentLoading(false);
                });
            } else {
              setIsContentLoading(false);
            }

          }
        )
        .catch((error) => {
          setDataFetchingError(error);
          setIsContentLoading(false);
        })
    };

    init();
  }, [credentialId, originClient, signinRedirect ]);

  const executeFormAction = async ({
    credentialId,
    action,
    apiMethodToCall,
    updateCredentialsPayload,
    newCredentialsState,
    newPendingActionsState,
    valueName,
    setErrors,
  }: {
    credentialId: string;
    action: string;
    apiMethodToCall: Function;
    updateCredentialsPayload?: UpdateUserCredentialsRequest;
    newCredentialsState?: CredentialResponse;
    newPendingActionsState?: PendingActions;
    valueName?: string;
    setErrors?: any;
  }) => {
    updateFormAction(action, FormActionStatus.IN_PROGRESS);
    try {
      const response = await apiMethodToCall.call(
        userCredentialsApi,
        credentialId,
        updateCredentialsPayload
      );
      updateFormAction(action, FormActionStatus.DONE);
      setErrorOutlines({});
      setAlertMessageProps(null);
      setTimeout(() => {
        updateFormAction(action, FormActionStatus.NO_ACTION);
        newCredentialsState && setUserCredentials(newCredentialsState);
        newPendingActionsState &&
          setUserCredentialsPendingActions(newPendingActionsState);
      }, 1000);

      await handleTabDataReload();

      return response;
    } catch (error) {
      console.error(`Api method "${apiMethodToCall.name}" failed: ${error}`);

      updateFormAction(action, FormActionStatus.ERROR);
      setTimeout(() => {
        updateFormAction(action, FormActionStatus.NO_ACTION);
      }, 2000);
      if (valueName && error instanceof ValidationError) {
        setErrors({
          [valueName]: error.getReasons().join("<br/>"),
        });
      }

      if (valueName && error instanceof NetworkError) {
        setErrorOutlines((prevVal:any) => ({
          ...prevVal,
          [valueName]: true
        }));
        setAlertMessageProps({
          type: "error",
          text: "Invalid characters or combination of characters have been used."
        });
        return;
      }
    }
  };

  const handleTabDataReload = async () => {
    if (!userCredentials) return;

    const credentialId = userCredentials.id;

    Promise.all([
      userCredentialsApi.getUserActivity(
        credentialId,
        (activeUserActivityPage - 1) * defaultLimit
      ),
      userCredentialsApi.getChangeLogs(
        credentialId,
        (activeChangeLogPage - 1) * defaultLimit
      ),
    ]).then(([userActivityResponse, changeLogs]) => {
      setUserActivity(userActivityResponse);
      setChangeLogs(changeLogs);
    });
  };

  if (isContentLoading) {
    return <Loading />;
  } else if (dataFetchingError) {
    return (
      <div className={classes.errorBoxWrapper}>
        <ErrorPage error={dataFetchingError} />
      </div>
    );
  } else {
    return (
    <Fragment>
        {credentialId && userCredentials && (
        <Grid item container xs={8}
        justifyContent="center"
        direction="row"
        sx={{boxShadow: 3}}
        className={classes.contentHolder}
          >
            <Grid className={classes.heading} item container xs={8}>
              <Grid container columnSpacing={8} rowSpacing={1}>

                <Grid item xs={10}>
                  <h1 className={classes.loginSupportTitle}>Login Support</h1>
                  <UserInfoSection
                    {...{
                      userCredentials,
                    }}
                  />
                </Grid>               
                {
                  alertMessageProps &&
                  <Grid item xs={12}>
                    <AlertMessage {...alertMessageProps}/>
                  </Grid>
                }
                <Grid item xs={10} sm={6}>
                  <LoginUsernameForm
                    {...{
                      userCredentials,
                      formActionsStatus,
                      executeFormAction,
                      userPermissions,
                      userCredentialsApi,
                      errorOutlines,
                      setErrorOutlines
                    }}
                  />
                  <PasswordAndMFASection
                    {...{
                      userCredentials,
                      userPermissions,
                      formActionsStatus,
                      executeFormAction,
                      userCredentialsApi,
                      brand,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LoginStatusList
                    {...{
                      userCredentials,
                    }}
                  />
                  <LoginEmailForm
                    {...{
                      userCredentials,
                      updateFormAction,
                      userCredentialsApi,
                      activeEmail,
                      setActiveEmail,
                      setUserCredentials,
                      setUserCredentialsPendingActions,
                      handleTabDataReload,
                      userPermissions,
                      isOktaUser,
                      formActionsStatus,
                      setIsMarkVerifiedCollapseOpened,
                      executeFormAction,
                      brand,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            {!isOktaUser && (
              <MarkVerifiedSection
                {...{
                  userCredentials,
                  userPermissions,
                  activeEmail,
                  executeFormAction,
                  userCredentialsApi,
                  userCredentialsPendingActions,
                  isMarkVerifiedCollapseOpened,
                  setIsMarkVerifiedCollapseOpened,
                }}
              />
            )}

            <LoginSupportTabs
              {...{
                userCredentialsApi,
                userCredentials,
                userPermissions,
                predefinedQuestions,
                userCredentialsPendingActions,
                setUserCredentials,
                setUserCredentialsPendingActions,
                userActivity,
                setUserActivity,
                changeLogs,
                setChangeLogs,
                activeUserActivityPage,
                setActiveUserActivityPage,
                activeChangeLogPage,
                setActiveChangeLogPage,
                handleTabDataReload,
                brand,
                activeAnswer,
                setActiveAnswer,
                setAlertMessageProps,
                errorOutlines,
                setErrorOutlines
              }}
            />
         
          </Grid>
        )}
      </Fragment>
    );
  }
};
export default SupportModalComponent;
